import { sortProductsByExpiryAlert } from "./productExpiryHelpers";

export const DEFAULT_PRODUCT_PAGE_SIZE = 20;

export const resolveProductListHasMore = (paginate, loadedCount) => {
  if (!paginate) {
    return false;
  }

  const totalItems = Number(paginate.totalItems ?? paginate.total) || 0;
  const itemsPerPage =
    Number(paginate.itemsPerPage ?? paginate.per_page) ||
    DEFAULT_PRODUCT_PAGE_SIZE;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const nextPage = Number(paginate.nextPage);

  if (totalItems > 0) {
    return loadedCount < totalItems && nextPage <= totalPages;
  }

  return loadedCount > 0 && loadedCount % itemsPerPage === 0;
};

export const shouldFillProductListViewport = (hasMore) => {
  if (!hasMore || typeof window === "undefined") {
    return false;
  }

  const doc = document.documentElement;
  return doc.scrollHeight <= window.innerHeight + 1;
};

export const appendProductListParams = (data, params = {}) => {
  data.append("search", params.searchText ?? "");
  data.append("filter", params.filter ?? 1);

  if (params.selectedCat) {
    data.append("category_id", params.selectedCat);
  }
  if (params.selectedBrand) {
    data.append("brand_id", params.selectedBrand);
  }
  if (params.subCat) {
    data.append("company_category_id", params.subCat);
  }
  if (params.selectedSubcategory) {
    data.append("subcategory_id", params.selectedSubcategory);
  }
  if (params.selectedWarehouse) {
    data.append("warehouse_id", params.selectedWarehouse);
  }
  if (params.onlyAvailable) {
    data.append("existing", 1);
  }
  if (params.productSort) {
    data.append("sort", params.productSort);
  }
  if (params.priceFrom !== "" && params.priceFrom != null) {
    data.append("price_from", params.priceFrom);
  }
  if (params.priceTo !== "" && params.priceTo != null) {
    data.append("price_to", params.priceTo);
  }

  return data;
};

export const buildProductListPayload = (params = {}) => {
  const payload = {
    search: params.searchText ?? "",
    filter: params.filter == null ? true : Boolean(Number(params.filter)),
  };

  if (params.selectedCat) {
    payload.category_id = params.selectedCat;
  }
  if (params.selectedBrand) {
    payload.brand_id = params.selectedBrand;
  }
  if (params.subCat) {
    payload.company_category_id = params.subCat;
  }
  if (params.selectedSubcategory) {
    payload.subcategory_id = params.selectedSubcategory;
  }
  if (params.selectedWarehouse) {
    payload.warehouse_id = params.selectedWarehouse;
  }
  if (params.onlyAvailable) {
    payload.existing = 1;
  }
  if (params.productSort) {
    payload.sort = params.productSort;
  }
  if (params.priceFrom !== "" && params.priceFrom != null) {
    payload.price_from = params.priceFrom;
  }
  if (params.priceTo !== "" && params.priceTo != null) {
    payload.price_to = params.priceTo;
  }

  return payload;
};

export const appendProductListSearchParams = (
  params = {},
  searchParams = new URLSearchParams()
) => {
  if (params.filter) {
    searchParams.set("filter", "1");
  }

  if (params.searchText) {
    searchParams.set("search", params.searchText);
  }
  if (params.selectedCat) {
    searchParams.set("category_id", params.selectedCat);
  }
  if (params.selectedBrand) {
    searchParams.set("brand_id", params.selectedBrand);
  }
  if (params.subCat) {
    searchParams.set("company_category_id", params.subCat);
  }
  if (params.selectedSubcategory) {
    searchParams.set("subcategory_id", params.selectedSubcategory);
  }
  if (params.selectedWarehouse) {
    searchParams.set("warehouse_id", params.selectedWarehouse);
  }
  if (params.onlyAvailable) {
    searchParams.set("existing", "1");
  }
  if (params.productSort) {
    searchParams.set("sort", params.productSort);
  }
  if (params.priceFrom !== "" && params.priceFrom != null) {
    searchParams.set("price_from", params.priceFrom);
  }
  if (params.priceTo !== "" && params.priceTo != null) {
    searchParams.set("price_to", params.priceTo);
  }

  return searchParams;
};

export const buildProductListUrl = (basePath, params = {}, paging = {}) => {
  const searchParams = appendProductListSearchParams(params);

  if (paging.page) {
    searchParams.set("page", paging.page);
  }
  if (paging.count) {
    searchParams.set("count", paging.count);
  }

  const query = searchParams.toString();
  return query ? `${basePath}?${query}` : basePath;
};

export const hasActiveProductFilter = ({
  selectedCat,
  selectedBrand,
  subCat,
  selectedSubcategory,
  selectedWarehouse,
  onlyAvailable,
}) => {
  return Boolean(
    selectedCat ||
      selectedBrand ||
      subCat ||
      selectedSubcategory ||
      selectedWarehouse ||
      onlyAvailable
  );
};

export const orderProductListForDisplay = (products, productSort) => {
  if (productSort) {
    return products;
  }
  return sortProductsByExpiryAlert(products);
};

export const editOrderProductListForDisplay = (products, productSort) => {
  if (productSort) {
    return products;
  }

  const inOrder = [];
  const notInOrder = [];

  products.forEach((product) => {
    const count = Number(product.num ?? product.order_count ?? 0);
    if (count > 0) {
      inOrder.push(product);
    } else {
      notInOrder.push(product);
    }
  });

  return [
    ...sortProductsByExpiryAlert(inOrder),
    ...sortProductsByExpiryAlert(notInOrder),
  ];
};

export const mergeEditOrderLocalState = (products, prevList) => {
  if (!prevList?.length) {
    return products;
  }

  const prevById = new Map(prevList.map((product) => [product.id, product]));

  return products.map((product) => {
    const prev = prevById.get(product.id);
    if (!prev) {
      return product;
    }

    return {
      ...product,
      num: prev.num,
      unitCounts: prev.unitCounts,
      selectedUnitLevel: prev.selectedUnitLevel,
      unit_price: prev.unit_price,
      customUnitPrices: prev.customUnitPrices,
    };
  });
};

export const hasActiveProductSort = ({ productSort, priceFrom, priceTo }) => {
  return Boolean(productSort || priceFrom || priceTo);
};
